import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export interface Tab {
  id: string;
  name: string;
  order: number;
  cameraPosition: { x: number; y: number; z: number };
  modelPath?: string;
  createdAt: string;
}

export interface SceneMetadata {
  version: string;
  createdAt: string;
  lastModifiedAt: string;
}

interface SceneState {
  tabs: Tab[];
  activeTabId: string | null;
  metadata: SceneMetadata;
  isDirty: boolean;
  lastSavedAt: string | null;
}

interface SceneActions {
  createTab: () => void;
  deleteTab: (id: string) => void;
  renameTab: (id: string, name: string) => void;
  reorderTabs: (tabId: string, newOrder: number) => void;
  duplicateTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  setModelPath: (tabId: string, modelPath: string) => void;
  markDirty: () => void;
  markClean: () => void;
}

type SceneStore = SceneState & SceneActions;

const MAX_TABS = 10;
const DEFAULT_CAMERA_POSITION = { x: 0, y: 1.5, z: 5 };

const generateTabId = (): string => {
  return `tab-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

const createDefaultTab = (order: number): Tab => {
  return {
    id: generateTabId(),
    name: `Scene ${order + 1}`,
    order,
    cameraPosition: { ...DEFAULT_CAMERA_POSITION },
    createdAt: new Date().toISOString(),
  };
};

export const useSceneStore = create<SceneStore>()(
  immer((set) => ({
    // Initial state
    tabs: [createDefaultTab(0)],
    activeTabId: null,
    metadata: {
      version: '1.0.0',
      createdAt: new Date().toISOString(),
      lastModifiedAt: new Date().toISOString(),
    },
    isDirty: false,
    lastSavedAt: null,

    // Actions
    createTab: () =>
      set((state) => {
        // Enforce max 10 tabs
        if (state.tabs.length >= MAX_TABS) {
          console.warn(`Maximum of ${MAX_TABS} tabs allowed`);
          return;
        }

        const newOrder = state.tabs.length;
        const newTab = createDefaultTab(newOrder);

        state.tabs.push(newTab);
        state.activeTabId = newTab.id;
        state.metadata.lastModifiedAt = new Date().toISOString();
        state.isDirty = true;
      }),

    deleteTab: (id: string) =>
      set((state) => {
        // Prevent deleting the last tab
        if (state.tabs.length <= 1) {
          console.warn('Cannot delete the last remaining tab');
          return;
        }

        const tabIndex = state.tabs.findIndex((tab) => tab.id === id);
        if (tabIndex === -1) {
          console.warn(`Tab with id ${id} not found`);
          return;
        }

        // Remove the tab
        state.tabs.splice(tabIndex, 1);

        // Reorder remaining tabs
        state.tabs.forEach((tab, index) => {
          tab.order = index;
        });

        // Update active tab if the deleted tab was active
        if (state.activeTabId === id) {
          // Set active to the previous tab, or the first tab if we deleted the first one
          const newActiveIndex = Math.max(0, tabIndex - 1);
          state.activeTabId = state.tabs[newActiveIndex]?.id || null;
        }

        state.metadata.lastModifiedAt = new Date().toISOString();
        state.isDirty = true;
      }),

    renameTab: (id: string, name: string) =>
      set((state) => {
        const tab = state.tabs.find((t) => t.id === id);
        if (!tab) {
          console.warn(`Tab with id ${id} not found`);
          return;
        }

        // Validate name length (max 30 characters as per spec)
        const trimmedName = name.trim().substring(0, 30);
        if (!trimmedName) {
          console.warn('Tab name cannot be empty');
          return;
        }

        tab.name = trimmedName;
        state.metadata.lastModifiedAt = new Date().toISOString();
        state.isDirty = true;
      }),

    reorderTabs: (tabId: string, newOrder: number) =>
      set((state) => {
        const tabIndex = state.tabs.findIndex((tab) => tab.id === tabId);
        if (tabIndex === -1) {
          console.warn(`Tab with id ${tabId} not found`);
          return;
        }

        // Validate new order
        if (newOrder < 0 || newOrder >= state.tabs.length) {
          console.warn('Invalid tab order');
          return;
        }

        // Remove tab from current position
        const [movedTab] = state.tabs.splice(tabIndex, 1);

        // Insert at new position
        state.tabs.splice(newOrder, 0, movedTab);

        // Update order values for all tabs
        state.tabs.forEach((tab, index) => {
          tab.order = index;
        });

        state.metadata.lastModifiedAt = new Date().toISOString();
        state.isDirty = true;
      }),

    duplicateTab: (id: string) =>
      set((state) => {
        // Enforce max 10 tabs
        if (state.tabs.length >= MAX_TABS) {
          console.warn(`Maximum of ${MAX_TABS} tabs allowed`);
          return;
        }

        const tabToDuplicate = state.tabs.find((tab) => tab.id === id);
        if (!tabToDuplicate) {
          console.warn(`Tab with id ${id} not found`);
          return;
        }

        const newOrder = state.tabs.length;
        const duplicatedTab: Tab = {
          id: generateTabId(),
          name: `${tabToDuplicate.name} Copy`,
          order: newOrder,
          cameraPosition: { ...tabToDuplicate.cameraPosition },
          modelPath: tabToDuplicate.modelPath,
          createdAt: new Date().toISOString(),
        };

        state.tabs.push(duplicatedTab);
        state.activeTabId = duplicatedTab.id;
        state.metadata.lastModifiedAt = new Date().toISOString();
        state.isDirty = true;
      }),

    setActiveTab: (id: string) =>
      set((state) => {
        const tab = state.tabs.find((t) => t.id === id);
        if (!tab) {
          console.warn(`Tab with id ${id} not found`);
          return;
        }

        state.activeTabId = id;
        state.metadata.lastModifiedAt = new Date().toISOString();
        // Note: Switching tabs doesn't mark scene as dirty
      }),

    setModelPath: (tabId: string, modelPath: string) =>
      set((state) => {
        const tab = state.tabs.find((t) => t.id === tabId);
        if (!tab) {
          console.warn(`Tab with id ${tabId} not found`);
          return;
        }

        tab.modelPath = modelPath;
        state.metadata.lastModifiedAt = new Date().toISOString();
        state.isDirty = true;
      }),

    markDirty: () =>
      set((state) => {
        state.isDirty = true;
      }),

    markClean: () =>
      set((state) => {
        state.isDirty = false;
        state.lastSavedAt = new Date().toISOString();
      }),
  }))
);

// Initialize active tab to the first tab on store creation
useSceneStore.setState((state) => ({
  ...state,
  activeTabId: state.tabs[0]?.id || null,
}));
